// =============================================================================
// DataLoader — fetches /data/manifest, decides if cached bundle is stale,
// and pulls /data/all when needed. Persists in IndexedDB (~325 KB / ~70 KB
// gzip) keyed by ETag.
//
// Recommended usage:
//   const data = await dataLoader.load();
//   const species = data.species[someId];
//
// On focus / wake-up you can call `dataLoader.revalidate()` to re-probe the
// manifest cheaply (returns true if a new bundle was pulled).
//
// See docs/DATA.md.
// =============================================================================

import type { ApiClient } from "./ApiClient";
import type { DataBundle, DataManifest } from "@game/types";

const DB_NAME = "digimon";
const STORE = "static-data";
const KEY = "v1";

interface CachedBundle {
  etag: string;
  data: DataBundle;
  /** ms epoch when stored. */
  cachedAt: number;
}

export class DataLoader {
  private cache: CachedBundle | null = null;
  private inFlight: Promise<DataBundle> | null = null;

  constructor(private readonly api: ApiClient) {}

  /** Load the bundle (cached or freshly fetched). */
  async load(): Promise<DataBundle> {
    if (this.cache) return this.cache.data;
    if (this.inFlight) return this.inFlight;
    this.inFlight = (async () => {
      try {
        // Try IndexedDB first
        const fromDb = await readCache().catch(() => null);
        if (fromDb) this.cache = fromDb;

        // Probe manifest
        const headers: Record<string, string> = {};
        if (this.cache) headers["if-none-match"] = quote(this.cache.etag);
        try {
          const manifest = await this.api.get<DataManifest>("/data/manifest", { headers, noAuth: true });
          if (this.cache && manifest.etag === this.cache.etag) {
            return this.cache.data;
          }
          // ETag changed → pull full bundle
          const full = await this.api.get<DataBundle>("/data/all", { noAuth: true });
          this.cache = { etag: manifest.etag, data: full, cachedAt: Date.now() };
          await writeCache(this.cache).catch(() => undefined);
          return full;
        } catch (err) {
          // 304 is consumed by ApiClient as success — but if backend returned 304
          // (some HTTP libs throw), fall back to cached data.
          const status = (err as { status?: number }).status;
          if (status === 304 && this.cache) return this.cache.data;
          // Network error and we have cached data → use it
          if (this.cache) return this.cache.data;
          throw err;
        }
      } finally {
        this.inFlight = null;
      }
    })();
    return this.inFlight;
  }

  /**
   * Re-probe `/data/manifest` and pull a new bundle if the ETag changed.
   * Returns true if the bundle was rotated.
   */
  async revalidate(): Promise<boolean> {
    if (!this.cache) {
      await this.load();
      return true;
    }
    const headers: Record<string, string> = {
      "if-none-match": quote(this.cache.etag),
    };
    try {
      const manifest = await this.api.get<DataManifest>("/data/manifest", { headers, noAuth: true });
      if (manifest.etag === this.cache.etag) return false;
      const full = await this.api.get<DataBundle>("/data/all", { noAuth: true });
      this.cache = { etag: manifest.etag, data: full, cachedAt: Date.now() };
      await writeCache(this.cache).catch(() => undefined);
      return true;
    } catch (err) {
      const status = (err as { status?: number }).status;
      if (status === 304) return false;
      throw err;
    }
  }

  /** Snapshot of the current bundle without forcing a fetch. */
  current(): DataBundle | null {
    return this.cache?.data ?? null;
  }

  /** Drop the in-memory + persisted cache (useful for /admin or version bumps). */
  async clear(): Promise<void> {
    this.cache = null;
    await deleteCache().catch(() => undefined);
  }
}

// ---- IndexedDB shim --------------------------------------------------

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    if (typeof indexedDB === "undefined") {
      reject(new Error("indexedDB unavailable"));
      return;
    }
    const req = indexedDB.open(DB_NAME, 1);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) db.createObjectStore(STORE);
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

async function readCache(): Promise<CachedBundle | null> {
  try {
    const db = await openDb();
    return await new Promise<CachedBundle | null>((resolve, reject) => {
      const tx = db.transaction(STORE, "readonly");
      const req = tx.objectStore(STORE).get(KEY);
      req.onsuccess = () => resolve((req.result as CachedBundle | undefined) ?? null);
      req.onerror = () => reject(req.error);
    });
  } catch {
    return null;
  }
}

async function writeCache(payload: CachedBundle): Promise<void> {
  try {
    const db = await openDb();
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORE, "readwrite");
      tx.objectStore(STORE).put(payload, KEY);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  } catch {
    // ignore — fall back to memory only
  }
}

async function deleteCache(): Promise<void> {
  try {
    const db = await openDb();
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORE, "readwrite");
      tx.objectStore(STORE).delete(KEY);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  } catch {
    // ignore
  }
}

function quote(s: string): string {
  return s.startsWith('"') ? s : `"${s}"`;
}
